import { PermissionGate } from "@/modules/auth/presentation/ui/PermissionGate";
import { dashboardQuickActionsGridClassName } from "@/modules/dashboard/presentation/ui/dashboard-card-styles";
import { cn } from "@devhop/ui";
import { Link } from "@tanstack/react-router";
import {
  CalendarDaysIcon,
  FileTextIcon,
  LogInIcon,
  ShoppingCartIcon,
  Undo2Icon,
  type LucideIcon,
} from "lucide-react";

type DashboardQuickActionsProps = {
  pendingRefundCount?: number;
};

const actionTileClass =
  "group flex h-full min-h-[52px] w-full items-center gap-3 rounded-xl border bg-card p-3 transition-colors hover:border-primary/30 hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

function QuickActionTile({
  to,
  search,
  icon: Icon,
  label,
  badge,
}: {
  to: string;
  search?: Record<string, unknown>;
  icon: LucideIcon;
  label: string;
  badge?: number;
}) {
  return (
    <Link
      to={to}
      search={search as never}
      className={actionTileClass}
    >
      <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary transition-colors group-hover:bg-primary/15">
        <Icon className="size-4" aria-hidden />
      </span>
      <span className="flex min-w-0 flex-1 items-center gap-2">
        <span className="font-medium text-sm leading-snug">{label}</span>
        {badge != null && badge > 0 ? (
          <span className="rounded-full bg-destructive px-2 py-0.5 font-medium text-destructive-foreground text-xs tabular-nums">
            {badge}
          </span>
        ) : null}
      </span>
    </Link>
  );
}

export function DashboardQuickActions({
  pendingRefundCount,
}: DashboardQuickActionsProps) {
  return (
    <section>
      <div className={dashboardQuickActionsGridClassName}>
        <PermissionGate any={["classes:read", "private-lessons:read"]}>
          <QuickActionTile
            to="/app/attendance/check-in"
            icon={LogInIcon}
            label="ກວດຊື່"
          />
        </PermissionGate>
        <PermissionGate any={["packages:sell", "private-lessons:sell"]}>
          <QuickActionTile
            to="/app/sell"
            icon={ShoppingCartIcon}
            label="ຂາຍແພັກເກດ"
          />
        </PermissionGate>
        <PermissionGate all={["classes:read"]}>
          <QuickActionTile
            to="/app/classes/schedule"
            icon={CalendarDaysIcon}
            label="ປະຕິທິນ"
          />
        </PermissionGate>
        <PermissionGate all={["billing:read"]}>
          <QuickActionTile
            to="/app/billing/credit-invoices"
            icon={FileTextIcon}
            label="ໃບເກັບເງິນ"
          />
        </PermissionGate>
        <PermissionGate all={["refunds:read"]}>
          <QuickActionTile
            to="/app/refunds"
            search={{
              filters: [
                {
                  field: "status",
                  op: "eq",
                  value: "requested",
                },
              ],
            }}
            icon={Undo2Icon}
            label="ຄຳຂໍຄືນເງິນ"
            badge={pendingRefundCount}
          />
        </PermissionGate>
      </div>
    </section>
  );
}

export function DashboardChartEmptyState({
  className,
}: {
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex items-center justify-center rounded-lg bg-muted/40 text-muted-foreground text-sm",
        className,
      )}
    >
      ບໍ່ພົບຂໍ້ມູນ
    </div>
  );
}
